<template>
  <div>
    <ReservationBox>
      <template #header>
        <h2>Zadajte prosím počet cestujúcich osôb</h2>
      </template>

      <div class="button-radio-list">
        <button
          v-for="n in 4"
          :key="`number-of-travelelrs-button-${n}`"
          class="button-radio"
          type="button"
          :class="{ active: state.numberOfTravellers === n }"
          @click="setLocalNumberOfTravellers(n)"
        >
          <span>{{ n }}</span>

          <BartoIcon
            class="icon"
            :style="getWidthOfButtonRadio(n)"
            :icon="`reservation-traveller-${n}`"
          />
        </button>

        <button
          class="button-radio"
          type="button"
          :class="{
            active: Number(state.numberOfTravellers) > 4 || state.numberOfTravellers === '',
          }"
          @click="setInputOpen(true)"
        >
          <span>viac</span>

          <BartoIcon
            class="icon"
            style="width: 3rem; height: 1.5rem"
            icon="reservation-traveller-5"
          />
        </button>
      </div>

      <div v-show="state.inputOpen" class="row m-t-sm">
        <div class="col-8 col-sm-5">
          <div class="form-input">
            <label for="numberOfTravellers">Zadajte počet cestujúcich</label>

            <Field
              v-slot="{ field }: FieldSlot"
              v-model.number="state.numberOfTravellers"
              name="numberOfTravellers"
            >
              <input
                v-bind="field"
                id="numberOfTravellers"
                ref="numberOfTravellersInput"
                type="text"
                class="form-control"
              />

              <div v-show="meta.touched && errors.numberOfTravellers" class="text-danger">
                {{ errors.numberOfTravellers }}
              </div>
            </Field>
          </div>
        </div>
      </div>
    </ReservationBox>

    <ReservationBoxControls
      back-button-text="Späť na výber termínu"
      continue-button-text="Pokračovať"
      @return="onReturn"
      @continue="onContinue"
    />
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useForm, Field } from 'vee-validate';
import ReservationBox from '../components/ReservationBox.vue';
import ReservationBoxControls from '../components/ReservationBoxControls.vue';
import { useReservation } from '../modules/useReservation';
import { pushRoute, Route } from '../router';
import { useTripInfo } from '../modules/useTripInfo';
import { useLoader } from '../modules/useLoader';
import type { FieldSlot } from '../types';
import { tracking } from '../modules/analytics/analytics';
import { useReservationTime } from '../modules/useReservationTime';
import { useReservationTracking } from '../modules/useReservationTracking';
import BartoIcon from '../components/BartoIcon.vue';

const { reservationTimeState } = useReservationTime();
const { trackEvent, trackPageView } = tracking();
const { trackTripEvent } = useReservationTracking();
const { reservationState, setNumberOfTravellers } = useReservation();
const { url } = useTripInfo();
const { showLoader } = useLoader();

const state = reactive<{
  numberOfTravellers: number | string;
  inputOpen: boolean;
}>({
  numberOfTravellers: reservationState.value?.requested_travellers_count ?? 0,
  inputOpen: !!(
    reservationState.value?.requested_travellers_count &&
    reservationState.value?.requested_travellers_count > 4
  ),
});

const form = useForm({
  initialValues: {
    numberOfTravellers: state.numberOfTravellers,
  },
  validationSchema: {
    numberOfTravellers: { required: true, between: [1, 100] },
  },
});

const { errors, defineInputBinds, setFieldValue, validate, meta } = form;

const numberOfTravellersInputFormValue = defineInputBinds('numberOfTravellers');

watch(state, (newValue, oldValue) => {
  setFieldValue('numberOfTravellers', newValue.numberOfTravellers);
});

const numberOfTravellersInput = ref<HTMLInputElement | null>(null);

const getWidthOfButtonRadio = (n: number) => {
  let width = '1.6rem';

  if (n === 3) {
    width = '2rem';
  }

  if (n === 4) {
    width = '2.3rem';
  }

  return `width: ${width}; height: 1.5rem;`;
};

const setLocalNumberOfTravellers = (value: number) => {
  state.numberOfTravellers = value;
};

const setInputOpen = (value: boolean) => {
  state.inputOpen = value;
  state.numberOfTravellers = '';

  nextTick(() => {
    numberOfTravellersInput.value?.focus();
  });
};

const validateForm = () =>
  new Promise((resolve) => {
    validate({ mode: 'force' }).then(({ valid }) => {
      resolve(valid && state.numberOfTravellers && state.numberOfTravellers !== '');
    });
  });

const onReturn = () => {
  showLoader();
  window.location.href = url;
};

const onContinue = async () => {
  if (!(await validateForm())) {
    return;
  }

  await setNumberOfTravellers(state.numberOfTravellers as number);
  trackTripEvent({ event: 'begin_checkout', value: 0 });

  pushRoute(Route.TravellersData);
};
onMounted(() => {
  if (
    !reservationTimeState.timeUntilReservationEnds ||
    (reservationTimeState.timeUntilReservationEnds &&
      reservationTimeState.timeUntilReservationEnds < 0)
  ) {
    trackTripEvent({ event: 'add_to_cart', value: 0 });

    window.fbq('track', 'AddToCart', {
      currency: 'EUR',
      value:
        window.RESERVATION_STORE.prices.find((item) => item.category === 'CATEGORY_TRIP_FULL')
          ?.price ?? 0,
      content_name: window.RESERVATION_STORE.tripBaseInfo.name,
      content_type: 'product',
      content_ids: [String(window.RESERVATION_STORE.tripBaseInfo.id)],
    });
  }

  trackPageView({
    screen: 'RESERVATION_NUMBER_OF_TRAVELLERS',
  });
});
</script>
